import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  TextInput,
  ScrollView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const CancelSubscriptionScreen = ({ subscription, onCancel, onConfirm }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [selectedReason, setSelectedReason] = useState(null);

  const cancellationReasons = [
    { id: 'too_expensive', title: 'Too expensive', icon: 'currency-usd' },
    { id: 'not_using', title: 'Not using enough', icon: 'clock-outline' },
    { id: 'missing_features', title: 'Missing features', icon: 'feature-search' },
    { id: 'poor_support', title: 'Poor customer support', icon: 'help-circle-outline' },
    { id: 'switching_provider', title: 'Switching to another provider', icon: 'swap-horizontal' },
    { id: 'other', title: 'Other reason', icon: 'dots-horizontal' },
  ];

  const handleCancelSubscription = async () => {
    if (!selectedReason) {
      Alert.alert('Error', 'Please select a reason for cancellation.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/business-subscriptions/${subscription?.id}`, {
        method: 'PUT',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'cancel',
          reason: selectedReason,
          feedback,
        }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to cancel subscription');

      Alert.alert(
        'Subscription Cancelled',
        'Your subscription has been cancelled successfully. You will continue to have access until the end of your current billing period.',
        [{ text: 'OK', onPress: onConfirm }]
      );
    } catch (error) {
      console.error('Cancellation error:', error);
      Alert.alert('Error', error.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      alwaysBounceVertical={true} // iOS: allows overscroll even when content fits
    >
      <View style={styles.cancelHeader}>
        <MaterialCommunityIcons name="alert-circle" size={48} color="#EF4444" />
        <Text style={styles.cancelTitle}>Cancel Subscription</Text>
        <Text style={styles.cancelSubtitle}>
          We're sorry to see you go. Help us improve by telling us why you're cancelling.
        </Text>
      </View>

      <View style={styles.currentPlanInfo}>
        <Text style={styles.currentPlanTitle}>Current Plan</Text>
        <Text style={styles.currentPlanName}>
          {subscription?.plan_id?.replace('multi_', '').toUpperCase() || 'BUSINESS'} Plan
        </Text>
        <Text style={styles.currentPlanPrice}>£{subscription?.price_paid || 299}/month</Text>
        <Text style={styles.currentPlanAccess}>
          Access until: {subscription?.end_date ? new Date(subscription.end_date).toLocaleDateString() : 'N/A'}
        </Text>
      </View>

      <View style={styles.reasonsContainer}>
        <Text style={styles.reasonsTitle}>Why are you cancelling?</Text>
        {cancellationReasons.map((reason) => (
          <TouchableOpacity
            key={reason.id}
            style={[styles.reasonOption, selectedReason === reason.id && styles.reasonOptionSelected]}
            onPress={() => setSelectedReason(reason.id)}
          >
            <MaterialCommunityIcons
              name={reason.icon}
              size={20}
              color={selectedReason === reason.id ? '#4A90E2' : '#64748B'}
            />
            <Text style={[styles.reasonText, selectedReason === reason.id && styles.reasonTextSelected]}>
              {reason.title}
            </Text>
            {selectedReason === reason.id && (
              <MaterialCommunityIcons name="check" size={20} color="#4A90E2" />
            )}
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.feedbackContainer}>
        <Text style={styles.feedbackTitle}>Additional feedback (optional)</Text>
        <TextInput
          style={styles.feedbackInput}
          placeholder="Tell us how we can improve..."
          value={feedback}
          onChangeText={setFeedback}
          multiline
          numberOfLines={3}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.cancelActions}>
        <TouchableOpacity style={styles.keepSubscriptionButton} onPress={onCancel}>
          <Text style={styles.keepSubscriptionText}>Keep Subscription</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.confirmCancelButton, loading && styles.disabledButton]}
          onPress={handleCancelSubscription}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.confirmCancelText}>Cancel Subscription</Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  contentContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40, // extra space at the bottom to ensure comfortable scrolling
  },
  cancelHeader: {
    alignItems: 'center',
    marginBottom: 30,
  },
  cancelTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  cancelSubtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
  },
  currentPlanInfo: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 20,
    marginBottom: 30,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  currentPlanTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginBottom: 8,
  },
  currentPlanName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#4A90E2',
    marginBottom: 4,
  },
  currentPlanPrice: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 8,
  },
  currentPlanAccess: {
    fontSize: 14,
    color: '#64748B',
  },
  reasonsContainer: {
    marginBottom: 30,
  },
  reasonsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  reasonOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  reasonOptionSelected: {
    backgroundColor: '#F0F8FF',
    borderColor: '#4A90E2',
  },
  reasonText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 12,
    flex: 1,
  },
  reasonTextSelected: {
    color: '#4A90E2',
    fontWeight: '500',
  },
  feedbackContainer: {
    marginBottom: 30,
  },
  feedbackTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  feedbackInput: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#0F172A',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minHeight: 80,
  },
  cancelActions: {
    flexDirection: 'row',
    gap: 12,
  },
  keepSubscriptionButton: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  keepSubscriptionText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  confirmCancelButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
  },
  confirmCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  disabledButton: {
    backgroundColor: '#E2E8F0',
  },
});

export default CancelSubscriptionScreen;